import React, { useEffect, useState, useCallback } from "react";
import { NavLink, useNavigate, Outlet, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useUI } from "@/context/UIContext";
import { ROLE_LABELS } from "@/lib/constants";
import api from "@/lib/api";
import {
  LayoutDashboard, PackagePlus, Truck, FolderKanban, Boxes, ShoppingCart,
  Users as UsersIcon, ScrollText, Bell, Sun, Moon, Rows3, Rows2, LogOut,
  Search, ScanLine, Gauge, HardDrive, ShieldCheck, KeyRound, MonitorSmartphone,
} from "lucide-react";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { ScannerSheet } from "@/components/scanner/ScannerSheet";
import { MobileTabBar } from "@/components/MobileTabBar";

export const NAV = [
  { to: "/", label: "Command Center", icon: LayoutDashboard, perm: "nav.dashboard" },
  { to: "/inward", label: "Inward / Job Cards", icon: PackagePlus, perm: "nav.inward" },
  { to: "/outward", label: "Outward Dispatch", icon: Truck, perm: "nav.outward" },
  { to: "/projects", label: "Projects", icon: FolderKanban, perm: "nav.projects" },
  { to: "/inventory", label: "Inventory", icon: Boxes, perm: "nav.inventory" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart, perm: "nav.purchase_orders" },
  { to: "/workload", label: "Workload Balancer", icon: Gauge, perm: "nav.workload" },
  { to: "/audit", label: "Audit Trail", icon: ScrollText, perm: "nav.audit" },
  { to: "/users", label: "Users & Roles", icon: UsersIcon, perm: "nav.users" },
  { to: "/permissions", label: "Roles & Permissions", icon: ShieldCheck, adminOnly: true },
  { to: "/settings/storage", label: "Media Storage", icon: HardDrive, perm: "nav.storage" },
];

export default function Layout() {
  const { user, logout, can } = useAuth();
  const { theme, toggleTheme, compact, toggleDensity } = useUI();
  const navigate = useNavigate();
  const [unread, setUnread] = useState(0);
  const [search, setSearch] = useState("");
  const [pwOpen, setPwOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  useEffect(() => { if (params.get("scan")) { setScanOpen(true); params.delete("scan"); setParams(params, { replace: true }); } }, [params, setParams]);

  const loadUnread = useCallback(() => {
    api.get("/notifications").then((r) => setUnread(r.data.filter((n) => !n.is_read).length)).catch(() => {});
  }, []);

  useEffect(() => {
    loadUnread();
    const iv = setInterval(loadUnread, 20000);
    return () => clearInterval(iv);
  }, [loadUnread]);

  const doSearch = (e) => {
    e?.preventDefault();
    navigate(`/inward?q=${encodeURIComponent(search)}`);
  };

  useEffect(() => {
    const handler = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "n") { e.preventDefault(); navigate("/inward?new=1"); }
      else if (e.ctrlKey && e.key.toLowerCase() === "f") { e.preventDefault(); document.getElementById("global-search-input")?.focus(); }
      else if (e.altKey && e.key.toLowerCase() === "d") { e.preventDefault(); toggleDensity(); }
      else if (e.altKey && e.key.toLowerCase() === "t") { e.preventDefault(); toggleTheme(); }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [navigate, toggleDensity, toggleTheme]);

  const items = NAV.filter((n) => (n.adminOnly ? user.role === "admin" : can(n.perm)));

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground">
      {/* Sidebar */}
      <aside className="hidden w-60 shrink-0 flex-col border-r border-border bg-card md:flex">
        <div className="flex items-center gap-2 border-b border-border px-4 py-4">
          <img src="/logo-amax.png" alt="amax" className="h-8 w-auto shrink-0 dark:brightness-0 dark:invert" />
          <div className="h-8 w-px bg-border" />
          <div>
            <div className="font-heading text-base font-extrabold uppercase leading-none tracking-tight">RepairOps</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">Command Hub</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-2">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.slice(1).replace("/", "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-2.5 rounded-md px-3 ${compact ? "py-1.5 text-[13px]" : "py-2 text-sm"} font-medium transition-all ${
                  isActive
                    ? "bg-primary/15 text-primary border-l-2 border-primary"
                    : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <n.icon size={17} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/20 font-mono text-xs font-bold text-primary">
              {user.full_name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-xs font-semibold">{user.full_name}</div>
              <div className="truncate font-mono text-[10px] uppercase text-muted-foreground">{ROLE_LABELS[user.role]}</div>
            </div>
            <button onClick={() => navigate("/devices")} data-testid="my-devices-btn" className="text-muted-foreground hover:text-primary" title="My devices">
              <MonitorSmartphone size={16} />
            </button>
            <button onClick={() => setPwOpen(true)} data-testid="change-password-btn" className="text-muted-foreground hover:text-primary" title="Change password">
              <KeyRound size={16} />
            </button>
            <button onClick={logout} data-testid="logout-btn" className="text-muted-foreground hover:text-red-500" title="Logout">
              <LogOut size={16} />
            </button>
          </div>
        </div>
        {pwOpen && <ChangePasswordModal onClose={() => setPwOpen(false)} />}
      </aside>

      {/* Main */}
      <div className="flex min-w-0 flex-1 flex-col">
        {/* Command Bar */}
        <header className="sticky top-0 z-40 flex items-center gap-2 border-b border-border bg-card/90 px-3 py-2.5 backdrop-blur-md sm:gap-3 sm:px-4" style={{ paddingTop: "max(0.625rem, env(safe-area-inset-top))" }}>
          <img src="/logo-amax.png" alt="amax" className="h-6 w-auto shrink-0 md:hidden dark:brightness-0 dark:invert" />
          <form onSubmit={doSearch} className="relative flex-1 max-w-md">
            <Search size={15} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              id="global-search-input"
              data-testid="global-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Scan / search serial, job #, equipment…  (Ctrl+F)"
              className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </form>
          <button onClick={() => setScanOpen(true)} title="Scan a label with the camera" data-testid="scan-btn"
            className="hidden h-9 items-center gap-1.5 rounded-md border border-border px-3 text-xs font-semibold hover:bg-accent md:flex">
            <ScanLine size={16} /> <span className="hidden sm:inline">Scan</span>
          </button>
          <div className="ml-auto flex items-center gap-1.5">
            <button onClick={toggleDensity} data-testid="density-toggle-btn" title="Toggle density (Alt+D)"
              className="hidden h-9 w-9 md:flex items-center justify-center rounded-md border border-border hover:bg-accent">
              {compact ? <Rows3 size={16} /> : <Rows2 size={16} />}
            </button>
            <button onClick={toggleTheme} data-testid="theme-toggle-btn" title="Toggle theme (Alt+T)"
              className="hidden h-9 w-9 md:flex items-center justify-center rounded-md border border-border hover:bg-accent">
              {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            <button onClick={() => navigate("/notifications")} data-testid="notifications-btn" title="Notifications"
              className="relative flex h-9 w-9 items-center justify-center rounded-md border border-border hover:bg-accent">
              <Bell size={16} />
              {unread > 0 && (
                <span className="absolute -right-1 -top-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 font-mono text-[9px] font-bold text-white">
                  {unread}
                </span>
              )}
            </button>
          </div>
        </header>

        <main className={`flex-1 overflow-y-auto pb-24 md:pb-6 ${compact ? "p-3" : "p-3 sm:p-6"}`}>
          <Outlet />
        </main>
        <MobileTabBar items={items} onScan={() => setScanOpen(true)} onPassword={() => setPwOpen(true)} />
        {scanOpen && <ScannerSheet onClose={() => setScanOpen(false)} />}
      </div>
    </div>
  );
}
